import { Inject, Injectable } from '@angular/core';
import * as beaver from 'beaver-logger';
import { AppConfigService } from '../app-config/app-config.service';
import { CLoggerConstants } from './logger.constants';
import { TBeaverLogger, IBeaverLoggerMetaBuilderParams, IBeaverLoggerPayloadParams } from './beaver-logger.model';
import { DOCUMENT } from '@angular/common';
import { ELogLevel, ILogData } from './logger.model';
import { InitialConfigService } from '../app-config/initial-config.service';
import { environment } from '@env/environment';
import { IInitialConfig } from '../venue-config/initial-config.model';

@Injectable({
  providedIn: 'root'
})
export class LoggerService {
  private readonly loggerConstants = CLoggerConstants;
  private loggingUrl: string;
  private logger: TBeaverLogger | undefined;
  private environment: string;

  constructor(
    @Inject(DOCUMENT) private document: Document,
    private appConfigService: AppConfigService,
    private initialConfigService: InitialConfigService
  ) {
    this.environment = this.appConfigService.getEnvValue<string>('env') as string;
    this.loggingUrl = this.appConfigService.getEnvValue<string>('loggingUrl') as string;

    this.logger = this.bootstrapLogger();
  }

  /**
   * Submit a server side INFO log via the beaver logger
   * @param message main log information
   * @param extras optional log specific data
   */
  info(message: string, extras?: ILogData): void {
    if (!this.isLoggerEnabled(this.logger)) {
      return;
    }

    this.logger.info(message, this.getFormattedLogData(message, extras, ELogLevel.info));
  }

  /**
   * Submit a server side WARNING log via the beaver logger
   * @param message main log information
   * @param extras optional log specific data
   */
  warn(message: string, extras?: ILogData): void {
    if (!this.isLoggerEnabled(this.logger)) {
      return;
    }

    this.logger.warn(message, this.getFormattedLogData(message, extras, ELogLevel.warn));
  }

  /**
   * Submit a server side ERROR log via the beaver logger
   * @param message main log information
   * @param extras optional log specific data
   * @param flush optional flush logs immediately to the server
   */
  error(message: string, extras?: ILogData, flush = false): void {
    if (!this.isLoggerEnabled(this.logger)) {
      return;
    }

    this.logger.error(message, this.getFormattedLogData(message, extras, ELogLevel.error));

    if (flush) {
      this.flushLog();
    }
  }

  /**
   * Flushes all logs to the server side.
   * It's "recommended" not to call this manually as it will happen automatically after the configured interval
   * But may be useful e.g. immediately flush errors logs before browser closes
   */
  flushLog(): void {
    if (!this.isLoggerEnabled(this.logger)) {
      return;
    }

    this.logger.flush();
  }

  /**
   * Creates an instance of the beaver logger and set's up default values
   * @returns beaver logger instance
   */
  private bootstrapLogger(): TBeaverLogger | undefined {
    if (typeof beaver !== 'object') {
      return;
    }

    const logger = beaver.Logger({
      // URI to post logs to
      url: this.loggingUrl,
      // Enable or disable heartbeats, which run on an interval
      heartbeat: false,
      silent: true,
      flushInterval: this.loggerConstants.logFlushInterval,
      // Log performance stats from the browser automatically?
      logPerformance: false
    });

    logger.addMetaBuilder(() => this.getMetaConfig());
    logger.addPayloadBuilder(() => this.getPayloadBuilder());

    return logger;
  }

  /**
   * Get's information that is attached to each log's payload
   * @returns log specific shared information
   */
  private getPayloadBuilder(): IBeaverLoggerPayloadParams {
    /** Initial config is usually defined, but if a bootstrapping error is being logged it may be undefined. */
    const initialConfig = this.initialConfigService.getInitialConfig() as IInitialConfig | undefined;
    return {
      utc: new Date(),
      url: this.document.location.href,
      pageID: this.loggerConstants.pageID,
      venueSlug: initialConfig?.siteKey || 'unknown',
      checkoutSlug: initialConfig?.checkoutSlug || 'unknown'
    };
  }

  /**
   * Get's general information that is attached to the logging payload whenever the logs are flushed
   * Add on initial config information if present
   * @returns general app log information
   */
  private getMetaConfig(): IBeaverLoggerMetaBuilderParams {
    return {
      applicationName: this.loggerConstants.applicationName,
      platformInstance: this.environment,
      browserInfo: this.getBrowserInfo(),
      osInfo: this.getUserAgentInfo()
    };
  }

  /**
   * Attempts to get browser info based on user agent information
   * @returns compiled browser info
   */
  private getBrowserInfo(): string {
    try {
      const userAgent = this.getUserAgentInfo();
      const browserMatch = userAgent.match(/(opera|safari|chrome|firefox|msie)\/?\s*(\.?\d+(\.\d+)*)/i);
      if (!browserMatch) {
        throw 'unknown';
      }

      // eslint-disable-next-line no-useless-escape
      const version = userAgent.match(/version\/([\.\d]+)/i);
      if (version?.length && !!version[1]) {
        browserMatch[2] = version[1];
      }

      return [browserMatch[1], browserMatch[2]].join();
    } catch (err) {
      return 'unknown';
    }
  }

  /**
   * Get's the navigator user agent - separate for testing mocks
   * @returns browser user agent as a string
   */
  private getUserAgentInfo(): string {
    return navigator.userAgent;
  }

  /**
   * Formats the log data with common properties before it goes to the API. Adds log message to payload when not set.
   * @param message log message
   * @param data log information
   * @returns formatted log data
   */
  private getFormattedLogData(message: string, data: ILogData = {}, level: ELogLevel): ILogData {
    if (!data.message) {
      data.message = message;
    }

    data.level = level;

    return data;
  }

  private isLoggerEnabled(logger: TBeaverLogger | undefined): logger is TBeaverLogger {
    return !environment.test && !!logger;
  }
}
